import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { Observable, combineLatest, of } from 'rxjs';
import { map, startWith, switchMap } from 'rxjs/operators';
import { FirestoreDataService } from '../core/firestore-data.service';
import { Employee, CommissionParams, WorkshopWork } from '../core/models';

@Component({
  selector: 'app-commissions',
  templateUrl: './comissions.component.html',
  styleUrls: ['./comissions.component.scss'],
  standalone: false
})
export class ComissionsComponent implements OnInit {
  activeTab: 'reports' | 'employees' | 'parameters' = 'reports';

  // EMPLEADOS
  employees$: Observable<Employee[]>;
  employeeForm: FormGroup;
  isSavingEmployee = false;
  selectedEmployee: Employee | null = null;

  // PARÁMETROS
  paramsForm: FormGroup;
  isSavingParams = false;

  // REPORTES
  monthFilterControl = this.fb.control(this.getCurrentMonthYear());
  availableMonths: { label: string, value: string }[] = [];
  reportData$: Observable<any[]>;

  // Variables para la exportación CSV
  private currentMonthlyWorks: WorkshopWork[] = [];
  private currentMonthLabel: string = '';

  constructor(
    private firestoreService: FirestoreDataService,
    private fb: FormBuilder
  ) {
    // Inicializar Formularios
    this.employeeForm = this.fb.group({
      name: ['', Validators.required],
      type: ['Mecánico', Validators.required],
      isActive: [true]
    });

    // Ajustado a las 3 categorías solicitadas, manteniendo los otros en 0 ocultos por compatibilidad de TS
    this.paramsForm = this.fb.group({
      capacities: this.fb.group({
        Ride: [0, Validators.min(0)],
        Full: [0, Validators.min(0)],
        Ridewrap: [0, Validators.min(0)],
        Armado: [0], // Oculto
        Otros: [0]   // Oculto
      }),
      productsCommissionPercentage: [0.5, Validators.min(0)],
      eligibleProductCategories: ['ACCESORIOS PARA BICIS, PRESION Y NEUMATICOS, PIEZAS Y COMPONENTES, TRANSMISION, FRENOS, SUSPENCIONES'],
      assistantTiers: this.fb.array([]),
      mechanicTiers: this.fb.array([])
    });

    this.employees$ = this.firestoreService.getEmployees();
    this.generateMonthFilter();
    this.loadParams();

    // CÁLCULO DEL REPORTE OPTIMIZADO (Solo lee de DB el mes seleccionado)
    this.reportData$ = combineLatest([
      this.monthFilterControl.valueChanges.pipe(startWith(this.getCurrentMonthYear())),
      this.employees$,
      this.firestoreService.getCommissionParams()
    ]).pipe(
      switchMap(([monthValue, employees, params]) => {
        if (!params || !monthValue) return of([]);
        
        const [yearStr, monthStr] = monthValue.split('-');
        const year = parseInt(yearStr);
        const month = parseInt(monthStr);

        // Guardamos la etiqueta del mes para cuando descarguen el CSV (ej: "junio-2026")
        const dateObj = new Date(year, month - 1, 1);
        this.currentMonthLabel = dateObj.toLocaleString('es', { month: 'long', year: 'numeric' });

        // FETCH DIRECTO A FIREBASE (Menos lecturas)
        return this.firestoreService.getCompletedWorksByMonth(year, month).pipe(
          map(works => {
            this.currentMonthlyWorks = works; // Guardamos en memoria para el CSV
            return this.calculateCommissions(works, employees, params);
          })
        );
      })
    );
  }

  ngOnInit(): void {}

  getCurrentMonthYear(): string {
    const d = new Date();
    return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}`;
  }

  // --- REPORTES Y CÁLCULOS QUIRÚRGICOS ---
  private calculateCommissions(monthlyWorks: WorkshopWork[], employees: Employee[], params: CommissionParams) {
    const validCategories = params.eligibleProductCategories.map(c => c.trim().toUpperCase());

    // Referencias específicas pedidas por el usuario
    const ridewrapIds = ['264001','264002','264003','264004','264005','265001','265002','265003','265004','265005','265006','265011','265012','265013','265014','265015','265016','265017','265018','265019','265020','265021','265022','265023','265024','345129','345130','345133','345786','346335','346336','346337','346338','346339','346340','346341','346342'];
    const rideId = '234002';

    return employees.map(emp => {
      let totalWorksAssigned = 0;
      let eligibleWorksDone = 0;
      let totalCapacityNeeded = 0;
      let totalLaborSales = 0;
      let totalPartsSales = 0;

      // Un Set temporal para no contar el mismo trabajo dos veces en `totalWorksAssigned`
      const involvedWorks = new Set<string>();

      monthlyWorks.forEach(work => {
        // Encontrar TODOS los items en este trabajo asignados a ESTE empleado
        const employeeItems = work.addedItems?.filter(i => i.mechanicId === emp.id || i.assistantId === emp.id) || [];
        
        if (employeeItems.length === 0) return; // Si no hizo ningún item, lo saltamos
        involvedWorks.add(work.id || work.caseNumber);

        employeeItems.forEach(item => {
          let category: 'Ride' | 'Ridewrap' | 'Full' | 'Part' | null = null;

          // 1. CLASIFICACIÓN DEL ARTÍCULO
          if (ridewrapIds.includes(item.productId)) {
            category = 'Ridewrap';
          } else if (item.productId === rideId) {
            category = 'Ride';
          } else if (item.itemType === 'Labor') {
            // Toda mano de obra que no sea Ride es considerada FULL
            category = 'Full';
          } else {
            // Repuestos normales
            category = 'Part';
          }

          let isEligibleForLabor = false;

          // 2. REGLAS DE NEGOCIO POR ROL
          if (emp.type === 'Asistente Taller' && ['Ride', 'Ridewrap', 'Full'].includes(category)) {
            isEligibleForLabor = true;
          } else if (emp.type === 'Mecánico' && category === 'Full') {
            // El mecánico SOLO gana por Full
            isEligibleForLabor = true;
          }

          // 3. SUMAR VALORES
          if (isEligibleForLabor) {
            eligibleWorksDone++;
            // Sumar a la capacidad total necesaria usando el key de la categoría
            totalCapacityNeeded += params.capacities[category as keyof typeof params.capacities] || 0;
            totalLaborSales += (item.unitPrice * item.quantity);
          } else if (category === 'Part') {
            // Cálculo de comisión para Repuestos Normales
            if (item.productFamily && validCategories.includes(item.productFamily.toUpperCase())) {
              totalPartsSales += (item.unitPrice * item.quantity);
            }
          }
        });
      });

      totalWorksAssigned = involvedWorks.size;

      // Calcular Porcentaje de Capacidad
      const capacityPercentage = totalCapacityNeeded > 0 ? (eligibleWorksDone / totalCapacityNeeded) * 100 : 0;

      // Determinar el Tier (Nivel de comisión)
      const tiers = emp.type === 'Mecánico' ? params.mechanicTiers : params.assistantTiers;
      let applicableLaborCommPercent = 0;
      
      const sortedTiers = [...tiers].sort((a, b) => a.thresholdPercentage - b.thresholdPercentage);
      for (const tier of sortedTiers) {
        if (capacityPercentage >= tier.thresholdPercentage) {
          applicableLaborCommPercent = tier.commissionPercentage;
        }
      }

      // Calcular monto final
      const laborCommissionAmount = totalLaborSales * (applicableLaborCommPercent / 100);
      const partsCommissionAmount = totalPartsSales * (params.productsCommissionPercentage / 100);

      return {
        employee: emp,
        totalWorks: totalWorksAssigned,
        eligibleWorks: eligibleWorksDone,
        capacityReached: capacityPercentage,
        laborSales: totalLaborSales,
        laborCommissionRate: applicableLaborCommPercent,
        laborCommissionAmount: laborCommissionAmount,
        partsSales: totalPartsSales,
        partsCommissionRate: params.productsCommissionPercentage,
        partsCommissionAmount: partsCommissionAmount,
        totalCommission: laborCommissionAmount + partsCommissionAmount
      };
    });
  }

  // --- DESCARGAR REPORTE DETALLADO (CSV) ---
  downloadEmployeeReport(row: any) {
    const emp: Employee = row.employee;
    const csvRows = [];
    
    // Encabezados del archivo
    csvRows.push(['Empleado', 'Caso/Orden', 'Tipo Servicio General', 'Articulo / Mano de Obra Realizada','Tipo art.' ,'Precio Unitario', 'Cantidad', 'Total Cobrado', 'Fecha'].join(','));

    // Recorremos los trabajos del mes que ya tenemos en memoria
    this.currentMonthlyWorks.forEach(work => {
      const empItems = work.addedItems?.filter(i => i.mechanicId === emp.id || i.assistantId === emp.id) || [];
      
      empItems.forEach(item => {
        const dateStr = work.entryDate?.toDate ? work.entryDate.toDate().toLocaleDateString('es-DO') : '';
        const total = item.unitPrice * item.quantity;
        
        // Escapar textos por si llevan comas para no romper el CSV
        const cleanEmpName = `"${emp.name.replace(/"/g, '""')}"`;
        const cleanCase = `"${work.caseNumber}"`;
        const cleanService = `"${work.serviceType || 'N/A'}"`;
        const cleanProductName = `"${item.productName.replace(/"/g, '""')}"`;
        const cleanDate = `"${dateStr}"`;
        
        csvRows.push([
          cleanEmpName,
          cleanCase,
          cleanService,
          cleanProductName,
          item.itemType,
          item.unitPrice,
          item.quantity,
          total,
          cleanDate
        ].join(','));
      });
    });

    const csvString = csvRows.join('\n');
    // UFEFF es el BOM para que Excel lea correctamente ñ y tildes (UTF-8)
    const blob = new Blob(["\uFEFF" + csvString], { type: 'text/csv;charset=utf-8;' }); 
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    
    // Formatear el nombre del archivo: ej. jorge_perez-junio-2026.csv
    const monthName = this.currentMonthLabel.replace(/ de /g, '-').replace(/\s+/g, '').toLowerCase(); 
    const cleanEmpFileName = emp.name.replace(/\s+/g, '_').toLowerCase();
    
    link.setAttribute("href", url);
    link.setAttribute("download", `${cleanEmpFileName}-${monthName}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // --- GESTIÓN DE EMPLEADOS ---
  selectEmployee(emp: Employee) {
    this.selectedEmployee = emp;
    this.employeeForm.patchValue(emp);
  }

  resetEmployeeForm() {
    this.selectedEmployee = null;
    this.employeeForm.reset({ type: 'Mecánico', isActive: true });
  }

  async saveEmployee() {
    if (this.employeeForm.invalid) return;
    this.isSavingEmployee = true;
    try {
      if (this.selectedEmployee?.id) {
        await this.firestoreService.updateEmployee(this.selectedEmployee.id, this.employeeForm.value);
      } else {
        await this.firestoreService.addEmployee(this.employeeForm.value);
      }
      this.resetEmployeeForm();
    } catch (e) {
      console.error(e);
      alert("Error al guardar empleado");
    } finally {
      this.isSavingEmployee = false;
    }
  }

  async deleteEmployee(id: string) {
    if(confirm("¿Seguro que deseas eliminar este empleado?")) {
      await this.firestoreService.deleteEmployee(id);
      this.resetEmployeeForm();
    }
  }

  // --- GESTIÓN DE PARÁMETROS ---
  loadParams() {
    this.firestoreService.getCommissionParams().subscribe(params => {
      if (params) {
        this.paramsForm.patchValue({
          capacities: params.capacities,
          productsCommissionPercentage: params.productsCommissionPercentage,
          eligibleProductCategories: params.eligibleProductCategories.join(', ')
        });
        this.setTiers('assistantTiers', params.assistantTiers);
        this.setTiers('mechanicTiers', params.mechanicTiers);
      } else {
        this.setTiers('assistantTiers', [
          { thresholdPercentage: 0, commissionPercentage: 2.5 },
          { thresholdPercentage: 50, commissionPercentage: 3.5 },
          { thresholdPercentage: 80, commissionPercentage: 5.0 },
          { thresholdPercentage: 100, commissionPercentage: 7.5 }
        ]);
        this.setTiers('mechanicTiers', [
          { thresholdPercentage: 0, commissionPercentage: 5.0 },
          { thresholdPercentage: 50, commissionPercentage: 7.5 },
          { thresholdPercentage: 80, commissionPercentage: 10.0 },
          { thresholdPercentage: 100, commissionPercentage: 15.0 }
        ]);
      }
    });
  }

  get assistantTiers() { return this.paramsForm.get('assistantTiers') as FormArray; }
  get mechanicTiers() { return this.paramsForm.get('mechanicTiers') as FormArray; }

  setTiers(formArrayName: string, tiers: any[]) {
    const formArray = this.paramsForm.get(formArrayName) as FormArray;
    formArray.clear();
    tiers.forEach(t => formArray.push(this.fb.group({
      thresholdPercentage: [t.thresholdPercentage, Validators.required],
      commissionPercentage: [t.commissionPercentage, Validators.required]
    })));
  }

  addTier(formArrayName: 'assistantTiers' | 'mechanicTiers') {
    this[formArrayName].push(this.fb.group({ thresholdPercentage: [0], commissionPercentage: [0] }));
  }

  removeTier(formArrayName: 'assistantTiers' | 'mechanicTiers', index: number) {
    this[formArrayName].removeAt(index);
  }

  async saveParams() {
    if (this.paramsForm.invalid) return;
    this.isSavingParams = true;
    const val = this.paramsForm.value;
    const paramsToSave: CommissionParams = {
      capacities: val.capacities,
      productsCommissionPercentage: val.productsCommissionPercentage,
      eligibleProductCategories: val.eligibleProductCategories.split(',').map((s: string) => s.trim().toUpperCase()),
      assistantTiers: val.assistantTiers,
      mechanicTiers: val.mechanicTiers
    };

    try {
      await this.firestoreService.updateCommissionParams(paramsToSave);
      alert("Parámetros actualizados con éxito");
    } catch (e) {
      alert("Error al guardar parámetros");
    } finally {
      this.isSavingParams = false;
    }
  }

  private generateMonthFilter() {
    const today = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const label = d.toLocaleString('es', { month: 'long', year: 'numeric' });
      const value = `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}`;
      this.availableMonths.push({ label: label.charAt(0).toUpperCase() + label.slice(1), value });
    }
  }
}